import React from "react";

import { RouteFactory } from "./route-factory";

// Layouts base
import {
  BaseLayout,
  LoginDeveloperLayout,
  RegisterDeveloperLayout,
  LoginAdminLayout,
  DefaultLayout,
} from "../layouts";

// Register Pages
import {
  MakeRegisterDeveloperWithoutCompany,
  MakeRegisterDeveloperCompany,
  MakePlan,
  MakeContract,
  MakeAnalizing,
  MakeRegister,
  MakeConfirmRegister,
  MakeChooseRegisterType,
} from "../../../main/factories/pages/register/pages";

// Admin Pages
import {
  MakeSolicitationEdit,
  MakeSolicitationNew,
  MakeCompanyEdit,
  MakeAdminLogin,
  MakeAdminForgotPassword,
  MakeAdminRecoveryPassword,
} from "../../../main/factories/pages/admin";
import MakeIssList from "../../../main/factories/pages/admin/iss/list/iss-list-factory";
import MakeIssEdit from "../../../main/factories/pages/admin/iss/edit/iss-edit-factory";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import MakeTaxationCodeAdmin from "../../../main/factories/pages/admin/taxation-code/taxation-code-admin-factory";
import MakeCityAdapterConfig from "../../../main/factories/pages/admin/city-adapter-config/city-adapter-config-factory";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import MakeNbsIbscbsAdmin from "../../../main/factories/pages/admin/nbs-ibscbs/nbs-ibscbs-admin-factory";
import MakeRegrasTributarias from "../../../main/factories/pages/admin/regras-tributarias/regras-tributarias-factory";

// Developer Pages
import {
  MakeHomeDeveloper,
  MakeSignIn,
  MakeDevForgotPassword,
  MakeDevRecoveryPassword,
  MakeDevNewPassword,
  MakeIntro,
} from "../../../main/factories/pages/developer";

// Pages
import PageNotFound from "../../pages/register-account/page-not-found";
import ConfirmationEmail from "../../pages/register-account/confirmation-email";
import AdminBaseLayout from "../layouts/admin-base-layout";
import MakeComapaniesList from "../../../main/factories/pages/admin/companies/list";
import MakeSolicitationList from "../../../main/factories/pages/admin/solicitations/list";
import MakeCustomerForm from "../../../main/factories/pages/admin/customer/form";
import MakeHomeAdmin from "../../../main/factories/pages/admin/home";
import MakeCnaeList from "../../../main/factories/pages/admin/cnae/list";
import MakeCnaeViewEditNew from "../../../main/factories/pages/admin/cnae/edit/edit-factory";
import MakeSuperDevList from "../../../main/factories/pages/admin/super-devs/list";
import MakeSuperDevEditNew from "../../../main/factories/pages/admin/super-devs/edit/edit-factory";
import MakeIssueCreate from "../../../main/factories/pages/developer/issue";
import MakeIssueList from "../../../main/factories/pages/developer/issue/list";
import MakeDocumentsList from "../../../main/factories/pages/developer/documents/list";
import MakeRecurrencyList from "../../../main/factories/pages/developer/recurrency/list";
import MakeRecurrencyNotesList from "../../../main/factories/pages/developer/recurrency/notes";
import MakeRecurrencyMutation from "../../../main/factories/pages/developer/recurrency-mutation";
import ExitPage from "../../pages/register-account/exit";
import MakeDocumentsDeveloper from "../../../main/factories/pages/admin/companies/list/documents/list";
import MakeForeignNoteCreate from "../../../main/factories/pages/developer/foreign-note";
import MakeForeignNoteList from "../../../main/factories/pages/developer/foreign-note/list";
import MakeInvoiceCreate from "../../../main/factories/pages/developer/invoice";
import MakeInvoiceList from "../../../main/factories/pages/developer/invoice/list";
import LinkedInPage from "../../../main/factories/pages/developer/linkedin";
import TermsUse from "../../pages/register-account/contract/terms-use";
import MakeIssueType from "../../../main/factories/pages/developer/issue-type";

const MakeProfile = React.lazy(
  () => import("../../../main/factories/pages/developer/profile"),
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CompaniesPage = React.lazy(
  () => import("../../pages/developer/companies"),
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CompanyDetailPage = React.lazy(
  () => import("../../pages/developer/companies/detail"),
);

function Routes(): JSX.Element {
  const routes = [
    {
      layout: DefaultLayout,
      pages: [
        { path: "/registrar", component: MakeRegister },
        {
          path: "/confirmacao-de-email",
          component: ConfirmationEmail,
        },
        {
          path: "/register-account/:code/:email",
          component: MakeConfirmRegister,
        },
        { path: "/termos-de-uso", component: TermsUse },
      ],
    },
    {
      layout: AdminBaseLayout,
      pages: [
        { path: "/admin/home", component: MakeHomeAdmin, exact: true },
        {
          path: "/admin/developers",
          component: MakeComapaniesList,
          exact: true,
        },
        {
          path: "/admin/developers/documents/:id",
          component: MakeDocumentsDeveloper,
          exact: true,
        },
        {
          path: "/admin/solicitations",
          component: MakeSolicitationList,
          exact: true,
        },
        {
          path: "/admin/customer/:id",
          component: MakeCustomerForm,
          exact: true,
        },
        {
          path: "/admin/developers/edit/:id",
          component: MakeCompanyEdit,
          exact: true,
        },
        {
          path: "/admin/developers/view/:id",
          component: MakeCompanyEdit,
          exact: true,
        },
        {
          path: "/admin/solicitation/edit/:id",
          component: MakeSolicitationEdit,
          exact: true,
        },
        {
          path: "/admin/solicitation/new",
          component: MakeSolicitationNew,
          exact: true,
        },
        {
          path: "/admin/cnae/list",
          component: MakeCnaeList,
          exact: true,
        },
        {
          path: "/admin/cnae/new",
          component: MakeCnaeViewEditNew,
          exact: true,
        },
        {
          path: "/admin/cnae/:id",
          component: MakeCnaeViewEditNew,
          exact: true,
        },
        {
          path: "/admin/super-dev/list",
          component: MakeSuperDevList,
          exact: true,
        },
        {
          path: "/admin/super-dev/new",
          component: MakeSuperDevEditNew,
          exact: true,
        },
        {
          path: "/admin/super-dev/:id",
          component: MakeSuperDevEditNew,
          exact: true,
        },
        {
          path: "/admin/iss",
          component: MakeIssList,
          exact: true,
        },
        {
          path: "/admin/iss/new",
          component: MakeIssEdit,
          exact: true,
        },
        {
          path: "/admin/iss/:id",
          component: MakeIssEdit,
          exact: true,
        },
        {
          path: "/admin/taxation-code",
          component: MakeTaxationCodeAdmin,
          exact: true,
        },
        {
          path: "/admin/city-adapter-config",
          component: MakeCityAdapterConfig,
          exact: true,
        },
        {
          path: "/admin/nbs-ibscbs",
          component: MakeNbsIbscbsAdmin,
          exact: true,
        },
        {
          path: "/admin/regras-tributarias",
          component: MakeRegrasTributarias,
          exact: true,
        },
      ],
    },
    {
      layout: RegisterDeveloperLayout,
      pages: [
        {
          path: "/cadastro-basico/1",
          component: MakeRegisterDeveloperWithoutCompany,
        },
        {
          path: "/cadastro-basico/2",
          component: MakePlan,
        },
        {
          path: "/cadastro-basico/3",
          component: MakeContract,
        },
        {
          path: "/cadastro-basico/4",
          component: MakeAnalizing,
        },
        {
          path: "/cadastrar-empresa/1",
          component: MakeRegisterDeveloperCompany,
        },
        {
          path: "/cadastrar-empresa/2",
          component: MakePlan,
        },
        {
          path: "/cadastrar-empresa/3",
          component: MakeContract,
        },
        {
          path: "/cadastrar-empresa/4",
          component: MakeAnalizing,
        },
        { path: "/cadastro", component: MakeChooseRegisterType },
      ],
    },
    {
      layout: BaseLayout,
      pages: [
        { path: "/home", component: MakeHomeDeveloper },
        { path: "/profile", component: MakeProfile },
        { path: "/companies", component: CompaniesPage },
        { path: "/companies/:id", component: CompanyDetailPage },
        { path: "/invoice", component: MakeInvoiceList },
        { path: "/invoice/create", component: MakeInvoiceCreate },
        { path: "/exterior", component: MakeForeignNoteList },
        { path: "/exterior/create", component: MakeForeignNoteCreate },
        { path: "/issue-type", component: MakeIssueType },
        { path: "/issue", component: MakeIssueList },
        { path: "/issue/create", component: MakeIssueCreate },
        { path: "/documents", component: MakeDocumentsList },
        { path: "/recurrency", component: MakeRecurrencyList },
        { path: "/recurrency/create", component: MakeRecurrencyMutation },
        { path: "/recurrency/:id", component: MakeRecurrencyMutation },
        { path: "/recurrency/:id/notes", component: MakeRecurrencyNotesList },
        { path: "/exit", component: ExitPage },
      ],
    },
    {
      layout: LoginAdminLayout,
      pages: [
        { path: "/admin", component: MakeAdminLogin },
        { path: "/admin/forgot-password", component: MakeAdminForgotPassword },
        {
          path: "/admin/recovery-password",
          component: MakeAdminRecoveryPassword,
        },
      ],
    },
    {
      layout: LoginDeveloperLayout,
      pages: [
        { path: "/", component: MakeSignIn },
        { path: "/intro", component: MakeIntro },
        {
          path: "/developer/forgot-password",
          component: MakeDevForgotPassword,
        },
        {
          path: "/developer/recovery-password",
          component: MakeDevRecoveryPassword,
        },
        {
          path: "/developer/new-password",
          component: MakeDevNewPassword,
        },
        { path: "/linkedin", component: LinkedInPage, exact: false },
        { path: "*", component: PageNotFound },
      ],
    },
  ];

  return <RouteFactory routes={routes} />;
}

export default Routes;
